import { useEffect, useRef, useState } from 'react';
import { API_URL, buildKioskAuthHeaders, getStoredAuthToken } from './api';
import './BecostSubscriptionSummary.css';

const labels = {
  native_active: 'Abonnement BECOST actif',
  native_cancelled: 'Abonnement BECOST résilié',
  payment_pending: 'Souscription ou paiement en attente dans BECOST',
  payment_required: 'Paiement à régulariser dans BECOST',
  trial: 'Essai BECOST en cours',
  manual_access: 'Accès BECOST accordé manuellement',
  ended: 'Essai ou abonnement BECOST terminé',
  none: 'Aucun abonnement enregistré dans BECOST',
  unknown: 'Situation commerciale à vérifier dans BECOST',
};
const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;
const exactKeys = (value, keys) => value && typeof value === 'object' && !Array.isArray(value)
  && Object.keys(value).length === keys.length && keys.every(key => Object.prototype.hasOwnProperty.call(value, key));
const isoDate = value => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)
  && Number.isFinite(Date.parse(value)) && new Date(value).toISOString() === value;
export function validBecostSummary(value, linkId) {
  if (!uuid.test(linkId || '') || value?.schemaVersion !== 1 || !['granted', 'required', 'unavailable'].includes(value.permission)) return false;
  if (value.permission !== 'granted') return exactKeys(value, ['schemaVersion', 'linkId', 'permission']) && (value.linkId === linkId || value.linkId === null);
  if (!exactKeys(value, ['schemaVersion', 'linkId', 'permission', 'summary']) || value.linkId !== linkId) return false;
  const summary = value.summary;
  return exactKeys(summary, ['state', 'managedByParent', 'checkedAt', 'providerVerifiedAt'])
    && Object.prototype.hasOwnProperty.call(labels, summary.state) && summary.managedByParent === false
    && isoDate(summary.checkedAt) && summary.providerVerifiedAt === null;
}
function accountId() {
  try { return String(JSON.parse(localStorage.getItem('kioskAccount') || 'null')?.id || ''); } catch (_) { return ''; }
}
const readSession = () => ({ token: getStoredAuthToken(), accountId: accountId() });
const sameSession = session => Boolean(session.token && session.accountId) && getStoredAuthToken() === session.token && accountId() === session.accountId;
const formatDate = value => new Date(value).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
const sessionMessage = 'Votre session a changé. Rechargez Mes options avant de consulter cet abonnement.';

function useBecostRead(linkId, path, validate) {
  const [session] = useState(readSession);
  const [changed, setChanged] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const controller = useRef(null);
  const currentLink = useRef(linkId);
  currentLink.current = linkId;
  const locked = changed || !sameSession(session);
  useEffect(() => {
    const check = () => {
      if (!sameSession(session)) { controller.current?.abort(); setChanged(true); setResult(null); setBusy(false); }
    };
    window.addEventListener('storage', check); window.addEventListener('focus', check);
    const timer = window.setInterval(check, 250);
    return () => { controller.current?.abort(); window.clearInterval(timer); window.removeEventListener('storage', check); window.removeEventListener('focus', check); };
  }, [session]);
  useEffect(() => {
    setResult(null); setBusy(false);
    return () => controller.current?.abort();
  }, [linkId]);
  async function load() {
    controller.current?.abort();
    const pending = new AbortController(); controller.current = pending;
    const isCurrent = () => !pending.signal.aborted && controller.current === pending && currentLink.current === linkId;
    setResult(null); setBusy(true);
    try {
      if (!sameSession(session) || changed) throw new Error('session');
      const response = await fetch(`${API_URL}/api/admin/integrations/becost/${path}`, {
        method: 'GET', headers: buildKioskAuthHeaders(), cache: 'no-store', signal: pending.signal,
      });
      if (!isCurrent()) return;
      if (!sameSession(session)) throw new Error('session');
      if (!response.ok) throw new Error('unavailable');
      const data = await response.json();
      if (!isCurrent()) return;
      if (!sameSession(session)) throw new Error('session');
      if (!validate(data, linkId)) throw new Error('unavailable');
      setResult({ linkId, data });
    } catch (_) {
      if (!isCurrent()) return;
      if (!sameSession(session)) { setChanged(true); setResult(null); }
      else setResult({ linkId, data: { permission: 'unavailable' } });
    } finally { if (isCurrent()) setBusy(false); }
  }
  const data = !locked && result?.linkId === linkId ? result.data : null;
  return { data, locked, busy, load };
}

export default function BecostSubscriptionSummary({ linkId }) {
  const { data, locked, busy, load } = useBecostRead(linkId, 'link/billing-status', validBecostSummary);
  if (!uuid.test(linkId || '')) return null;
  const summary = data?.permission === 'granted' ? data.summary : null;
  return <div className="becost-subscription-summary" role="group" aria-label="Abonnement BECOST">
    <h3>Votre abonnement BECOST</h3>
    <p>Consultez sa situation ici après avoir autorisé cette lecture dans BECOST.</p>
    {locked && <p className="becost-link-error" role="alert">{sessionMessage}</p>}
    {busy && !locked && <p role="status">Consultation de BECOST…</p>}
    {data?.permission === 'required' && <p role="status">Dans BECOST, ouvrez la liaison de cet établissement et autorisez le partage de l’état de votre abonnement. Revenez ensuite ici et actualisez.</p>}
    {data?.permission === 'unavailable' && <p className="becost-link-error" role="alert">Le statut de l’abonnement ne peut pas être confirmé. Réessayez ou consultez votre compte BECOST.</p>}
    {summary && <div role="status">
      <p><strong>{labels[summary.state]}</strong></p>
      <p>Données BECOST consultées le {formatDate(summary.checkedAt)}. Aucune confirmation fournisseur disponible dans ce résumé.</p>
      <p>Ce résumé utilise les données enregistrées dans BECOST. Il ne consulte pas Stripe en temps réel.</p>
    </div>}
    <button type="button" disabled={busy || locked} onClick={load}>{data ? 'Actualiser l’abonnement BECOST' : 'Voir mon abonnement BECOST'}</button>
    {!locked && <a className="product-options-open" href="https://becost.fr/app/beresto-link" target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">Gérer l’autorisation dans BECOST <span className="product-options-sr-only">(nouvel onglet)</span></a>}
    <p>Vos contrats natifs restent gérés dans BECOST. Un achat central se gère dans sa boutique BERESTO. Cette lecture n’autorise aucun achat, transfert d’abonnement ou accès aux données comptables.</p>
  </div>;
}

const centralLabels = {
  not_purchased: 'Aucun achat BECOST enregistré dans la boutique BERESTO pour cet établissement.',
  reservation_pending: 'Réservation BECOST en cours de vérification.',
  payment_pending: 'Paiement BECOST en attente.',
  activation_pending: 'Paiement reçu, activation BECOST en cours.',
  active: 'Abonnement BECOST actif depuis la boutique BERESTO.',
  suspended: 'Accès BECOST suspendu. Consultez votre abonnement en boutique.',
  ended: 'Abonnement BECOST terminé.',
  released: 'Demande BECOST clôturée sans abonnement actif.',
};
export function validBecostCommercial(value) {
  return exactKeys(value, ['schemaVersion', 'state', 'paidUntil', 'cancelAtPeriodEnd', 'brand']) && value.schemaVersion === 1
    && Object.prototype.hasOwnProperty.call(centralLabels, value.state)
    && (value.paidUntil === null || isoDate(value.paidUntil)) && typeof value.cancelAtPeriodEnd === 'boolean'
    && (value.state === 'not_purchased' ? value.brand === null : value.brand === 'beresto');
}
export function BecostCentralBilling({ linkId }) {
  const { data, locked, busy, load } = useBecostRead(linkId, 'commercial', validBecostCommercial);
  if (!uuid.test(linkId || '')) return null;
  const unavailable = data?.permission === 'unavailable';
  const canPrepare = data && ['not_purchased', 'released'].includes(data.state);
  return <div className="becost-subscription-summary" role="group" aria-label="BECOST depuis la boutique BERESTO">
    <h3>BECOST depuis la boutique BERESTO</h3>
    <p>37 € HT par mois pour une société BECOST existante, avec 10 utilisateurs et 20 € de crédits IA par mois inclus. Cette offre concerne les comptes récents éligibles dont l’essai est encore actif, sans contrat existant. Votre éligibilité sera vérifiée avant le paiement.</p>
    <p>Reliez votre société puis autorisez séparément les nouveaux achats dans BECOST. Cette autorisation ne déclenche aucun paiement.</p>
    {locked && <p className="becost-link-error" role="alert">{sessionMessage}</p>}
    {busy && !locked && <p role="status">Vérification de votre achat BECOST…</p>}
    {unavailable && <p className="becost-link-error" role="alert">La situation de votre achat ne peut pas être vérifiée. Consultez BECOST et réessayez avant de préparer une nouvelle souscription.</p>}
    {data && !unavailable && <div role="status"><p>{centralLabels[data.state]}</p>
      {data.paidUntil && <p>Période payée jusqu’au {formatDate(data.paidUntil)}.</p>}
      {data.cancelAtPeriodEnd && <p>Le renouvellement est arrêté à la fin de la période payée.</p>}
    </div>}
    <button type="button" disabled={busy || locked} onClick={load}>Vérifier mon achat BECOST en boutique</button>
    {!locked && <a className="product-options-open" href="https://becost.fr/app/beresto-link" target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">Autoriser les nouveaux achats dans BECOST <span className="product-options-sr-only">(nouvel onglet)</span></a>}
    {canPrepare && <><p>L’absence d’achat dans cette boutique ne confirme pas votre éligibilité. Connectez-vous à votre compte boutique BERESTO, puis sélectionnez l’établissement déjà relié à votre société. Aucun nouveau compte BECOST ne sera créé.</p><a className="product-options-open" href="https://beresto.fr/boutique.html?item=becost#product-becost" target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">Préparer mon achat BECOST <span className="product-options-sr-only">(nouvel onglet)</span></a></>}
    {data?.state === 'ended' && <><p>Ce contrat BECOST est terminé. Consultez votre compte BECOST ou contactez notre équipe pour examiner la suite de votre accès.</p><a className="product-options-open" href="https://becost.fr/app" target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">Consulter mon compte BECOST <span className="product-options-sr-only">(nouvel onglet)</span></a></>}
    {data?.state === 'active' && <a className="product-options-open" href="https://becost.fr/app" target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">Ouvrir BECOST avec ma connexion habituelle <span className="product-options-sr-only">(nouvel onglet)</span></a>}
    {data?.brand === 'beresto' && <a className="product-options-open" href="https://beresto.fr/boutique.html#mon-compte" target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">Gérer cet achat dans la boutique BERESTO <span className="product-options-sr-only">(nouvel onglet)</span></a>}
    <p>Vos contrats BECOST existants, notamment Stripe, restent gérés dans BECOST. Si leur situation doit être clarifiée, vérifiez votre compte BECOST avant tout nouvel achat. Cet achat ne connecte pas automatiquement les applications et ne partage pas les données comptables.</p>
  </div>;
}
